import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  styled,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Snackbar,
  Alert,
} from "@mui/material";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import moment from "moment";
import "moment/locale/pt";
import AddIcon from "@mui/icons-material/Add";
import { useAPI } from "app/hooks/useApi";

moment.locale("pt");

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[600],
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.selected,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const getStatusColor = (status, dataLimite) => {
  const hoje = moment().startOf('day');
  const limite = moment(dataLimite).startOf('day');
  
  if (status === 'CONCLUIDO') return 'success';
  if (limite.isBefore(hoje)) return 'error';
  if (limite.isSame(hoje)) return 'warning';
  if (limite.diff(hoje, 'days') <= 7) return 'warning';
  return 'info';
};

const getStatusText = (status, dataLimite) => {
  const hoje = moment().startOf('day');
  const limite = moment(dataLimite).startOf('day');
  
  if (status === 'CONCLUIDO') return 'Concluído';
  if (limite.isBefore(hoje)) return 'Vencido';
  if (limite.isSame(hoje)) return 'Vence hoje';
  
  const diasRestantes = limite.diff(hoje, 'days');
  if (diasRestantes <= 7) return `${diasRestantes} dias`;
  
  return 'Em prazo';
};

const ProcessProvidencias = ({ providencias = [], processId, numeroProcesso, onProvidenciaAdded }) => {
  const { makeRequest, loading } = useAPI();
  const [openModal, setOpenModal] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const [atualizandoAgenda, setAtualizandoAgenda] = useState(false);

  const [formData, setFormData] = useState({
    despacho_codigo: "",
    descricao: "",
    data_inicio: moment().format("YYYY-MM-DD"),
    observacao: "",
  });

  const codigosDespacho = [
    { codigo: "006", descricao: "Exigência a cumprir", prazo: "60 dias" },
    { codigo: "007", descricao: "Exigência formal a cumprir", prazo: "60 dias" },
    { codigo: "008", descricao: "Exigência formal a cumprir", prazo: "60 dias" },
    { codigo: "029", descricao: "Deferir o pedido", prazo: "60 dias" },
    { codigo: "030", descricao: "Deferir o pedido", prazo: "60 dias" },
    { codigo: "158", descricao: "Concessão de Registro", prazo: "10 anos" },
    { codigo: "159", descricao: "Exigência a cumprir", prazo: "60 dias + 30 dias extraordinário" },
    { codigo: "005", descricao: "Exigência formal", prazo: "5 dias" },
  ];

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setFormData({
      despacho_codigo: "",
      descricao: "",
      data_inicio: moment().format("YYYY-MM-DD"),
      observacao: "",
    });
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-preencher descrição quando selecionar código
    if (field === 'despacho_codigo') {
      const codigoSelecionado = codigosDespacho.find(c => c.codigo === value);
      if (codigoSelecionado) {
        setFormData(prev => ({
          ...prev,
          descricao: codigoSelecionado.descricao
        }));
      }
    }
  };

  const handleSave = async () => {
    try {
      if (!formData.despacho_codigo || !formData.data_inicio) {
        setSnackbar({
          open: true,
          message: "Código do despacho e data de início são obrigatórios",
          severity: "warning"
        });
        return;
      }

      const response = await makeRequest("/agenda", "POST", {
        processo_rpi_id: Number(processId), 
        despacho_codigo: formData.despacho_codigo,
        data_inicio: formData.data_inicio,
        observacao: formData.observacao || `Lançamento manual - ${formData.descricao}`
      });

      if (response.status === 200 || response.status === 201) {
        setSnackbar({
          open: true,
          message: "Providência criada com sucesso!",
          severity: "success"
        });
        
        handleCloseModal();
        
        if (onProvidenciaAdded) {
          onProvidenciaAdded();
        }
      }
    } catch (error) {

      setSnackbar({
        open: true,
        message: "Erro ao criar providência: " + (error.message || "Erro desconhecido"),
        severity: "error"
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const codigoSelecionado = codigosDespacho.find(c => c.codigo === formData.despacho_codigo);

  return (
    <>
      <Paper sx={{ my: 1, p: 2, width: '100%' }} elevation={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
          <Typography variant="h6">
            Providências
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleOpenModal}
              size="small"
            >
              Lançar Prazo
            </Button>
          </Box>
        </Box>
        
        {!providencias || providencias.length === 0 ? (
          <Box width="100%" p={2} textAlign="center">
            <Typography color="text.secondary">
              Nenhuma providência encontrada
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Box} sx={{ width: '100%' }}>
            <Table size="small" sx={{ width: "100%" }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Código</StyledTableCell>
                  <StyledTableCell>Descrição</StyledTableCell>
                  <StyledTableCell>Data Limite</StyledTableCell>
                  <StyledTableCell>Prazo</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {providencias.map((providencia) => (
                  <StyledTableRow key={providencia.id}>
                    <StyledTableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {providencia.despacho_codigo}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {providencia.descricao || '-'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {moment(providencia.data_limite).format("DD/MM/YYYY")}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {moment(providencia.data_limite).format("dddd")}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Typography variant="body2">
                        {providencia.prazo_dias ? `${providencia.prazo_dias} dias` : 
                         providencia.prazo_anos ? `${providencia.prazo_anos} anos` : '-'}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell>
                      <Chip
                        label={getStatusText(providencia.status, providencia.data_limite)}
                        color={getStatusColor(providencia.status, providencia.data_limite)}
                        size="small"
                        variant="outlined"
                      />
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Modal para lançamento manual de prazo */}
      <Dialog 
        open={openModal} 
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6">
            Lançar Providência Manual
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              {/* Processo */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Processo"
                  value={`Processo ID: ${processId}`}
                  disabled
                  variant="outlined"
                />
              </Grid>

              {/* Prazo Geração */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prazo Geração"
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => handleInputChange('data_inicio', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                />
              </Grid>

              {/* Prazo Oficial (calculado automaticamente) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Prazo Oficial"
                  value={
                    formData.data_inicio && codigoSelecionado
                      ? codigoSelecionado.codigo === "158"
                        ? moment(formData.data_inicio).add(10, 'years').format("DD/MM/YYYY")
                        : codigoSelecionado.codigo === "005"
                        ? moment(formData.data_inicio).add(5, 'days').format("DD/MM/YYYY")
                        : codigoSelecionado.codigo >= "159"
                        ? moment(formData.data_inicio).add(90, 'days').format("DD/MM/YYYY") // 60 + 30 extraordinário
                        : moment(formData.data_inicio).add(60, 'days').format("DD/MM/YYYY")
                      : ""
                  }
                  disabled
                  variant="outlined"
                />
              </Grid>

              {/* RPI */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="RPI"
                  placeholder="Número da RPI (opcional)"
                  variant="outlined"
                />
              </Grid>

              {/* Código do Despacho */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Despacho</InputLabel>
                  <Select
                    value={formData.despacho_codigo}
                    onChange={(e) => handleInputChange('despacho_codigo', e.target.value)}
                    label="Despacho"
                  >
                    {codigosDespacho.map((codigo) => (
                      <MenuItem key={codigo.codigo} value={codigo.codigo}>
                        {codigo.codigo} - {codigo.descricao}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Descrição */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Descrição"
                  multiline
                  rows={3}
                  value={formData.descricao}
                  onChange={(e) => handleInputChange('descricao', e.target.value)}
                  placeholder="Descrição da providência"
                  variant="outlined"
                />
              </Grid>

              {/* Responsável */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel>Responsável</InputLabel>
                  <Select
                    defaultValue=""
                    label="Responsável"
                  >
                    <MenuItem value="">Selecionar</MenuItem>
                    <MenuItem value="vivian">Vivian Barbosa</MenuItem>
                    <MenuItem value="admin">Administrador</MenuItem>
                    <MenuItem value="operador">Operador</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Comunicar ao responsável */}
              <Grid item xs={12} sm={6}>
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2">
                    Email será enviado para o responsável
                  </Typography>
                </Box>
              </Grid>

              {/* Informações sobre o prazo selecionado */}
              {codigoSelecionado && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Informações do Despacho Selecionado:
                    </Typography>
                    <Typography variant="body2">
                      <strong>Código:</strong> {codigoSelecionado.codigo}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Descrição:</strong> {codigoSelecionado.descricao}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Prazo:</strong> {codigoSelecionado.prazo}
                    </Typography>
                  </Paper>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="inherit">
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            color="primary"
            disabled={loading}
          >
            {loading ? "Salvando..." : "Gravar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default React.memo(ProcessProvidencias);
